// client/components/ui/TypBadge.jsx
import React, { useMemo } from "react";
import { useTheme } from "../../context/ThemeContext.jsx";
import PillBadge from "./PillBadge.jsx";

/* ===== Projekt-/Modul-Badges (bestehend) ===== */
const BASE_MAP = {
  elektro: {
    label: "Elektroprojekt",
    cls: "bg-sky-500/20 text-sky-200 border-sky-400/25"
  },
  pv: {
    label: "PV-Projekt",
    cls: "bg-yellow-500/20 text-yellow-200 border-yellow-400/25"
  },
  sicherheit: {
    label: "Sicherheitsprojekt",
    cls: "bg-rose-500/20 text-rose-200 border-rose-400/25"
  },
  ladestation: {
    label: "Ladestation-Projekt",
    cls: "bg-green-500/20 text-green-200 border-green-400/25"
  },
  lade: {
    label: "Ladestation-Projekt",
    cls: "bg-green-500/20 text-green-200 border-green-400/25"
  },
  intern: {
    label: "Internes Projekt",
    cls: "bg-zinc-500/20 text-zinc-200 border-zinc-400/25"
  },
  ladenbau: {
    label: "Ladenbau-Projekt",
    cls: "bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/25"
  },
  service: {
    label: "Serviceauftrag",
    cls: "bg-indigo-500/20 text-indigo-200 border-indigo-400/25"
  },
  offerte: {
    label: "Offerte",
    cls: "bg-blue-500/18 text-blue-200 border-blue-400/30"
  }
};
const FALLBACK_LABEL = "Projekt";
function getFallbackCls() {
  return "bg-white/10 text-blue-100 border-white/15";
}

/* ===== Helpers ===== */
const normTyp = t => String(t || "").trim().toLowerCase();

/* ===== Zusatz-Badges im gleichen Stil (Abwesenheiten & Status) ===== */
const ABSENZ_MAP = {
  ferien: {
    label: "Ferien",
    cls: "bg-sky-500/20 text-sky-200 border-sky-400/25"
  },
  kompensation: {
    label: "Kompensation",
    cls: "bg-slate-500/18 text-slate-100 border-slate-400/22"
  },
  vorholzeit: {
    label: "Vorholzeit",
    cls: "bg-violet-500/20 text-violet-200 border-violet-400/25"
  },
  feiertag: {
    label: "Feiertag",
    cls: "bg-indigo-500/20 text-indigo-200 border-indigo-400/25"
  },
  krankheit: {
    label: "Krankheit",
    cls: "bg-rose-500/20 text-rose-200 border-rose-400/25"
  },
  unfall: {
    label: "Unfall",
    cls: "bg-rose-500/20 text-rose-200 border-rose-400/25"
  },
  "militär": {
    label: "Militär",
    cls: "bg-zinc-500/20 text-zinc-200 border-zinc-400/25"
  },
  militaer: {
    label: "Militär",
    cls: "bg-zinc-500/20 text-zinc-200 border-zinc-400/25"
  }
};
const STATUS_MAP = {
  angenommen: {
    label: "angenommen",
    cls: "bg-green-500/20 text-green-200 border-green-400/25"
  },
  abgelehnt: {
    label: "abgelehnt",
    cls: "bg-rose-500/20 text-rose-200 border-rose-400/25"
  },
  "zurückgezogen": {
    label: "zurückgezogen",
    cls: "bg-amber-500/20 text-amber-200 border-amber-400/25"
  },
  offen: {
    label: "Offen",
    cls: null
  }
};

/* --- Abwesenheits-Typ --- */
export function AbsenzTypeBadge({
  typ,
  small = false,
  size = "auto",
  className = "",
  title: titleProp
}) {
    const key = normTyp(typ);
  const fallbackCls = getFallbackCls();
  const base = ABSENZ_MAP[key] || {
    label: typ || "Typ",
    cls: fallbackCls
  };
  const titleTxt = titleProp || base.label;
  return <PillBadge label={base.label} ariaLabel={`Typ: ${base.label}`} title={titleTxt} toneClassName={base.cls} size={size === "status" ? "status" : small ? "xs" : "sm"} className={className} />;
}

/* --- Status-Badge --- */
export function StatusBadge({
  status,
  small = false,
  size = "status",
  className = "",
  title: titleProp
}) {
    const key = normTyp(status || "offen");
  const base = STATUS_MAP[key] || {
    label: "Offen",
    cls: null
  };
  const isNeutral = !STATUS_MAP[key] || key === "offen";
  const cls = isNeutral ? getFallbackCls() : base.cls;
  const titleTxt = titleProp || base.label;
  return <PillBadge label={base.label} ariaLabel={`Status: ${base.label}`} title={titleTxt} toneClassName={cls} size={size === "status" ? "status" : small ? "xs" : "sm"} className={className} />;
}

/* --- Neutraler Fallback --- */
export function NeutralBadge({
  text = "—",
  small = false,
  size = "auto",
  className = ""
}) {
    const cls = getFallbackCls();
  return <PillBadge label={text} toneClassName={cls} size={size === "status" ? "status" : small ? "xs" : "sm"} className={className} />;
}

/* ===== Default: Projekt-/Modul-Badge (bestehend, kompatibel) ===== */
export default function TypBadge({
  typ,
  small = false,
  className = "",
  pulse = false,
  icon = null,
  mapOverride = null,
  title: titleProp,
  suffix,
  onlySuffix = false,
  size = "auto"
}) {
    const key = normTyp(typ);
  const MAP = useMemo(() => mapOverride ? {
    ...BASE_MAP,
    ...mapOverride
  } : BASE_MAP, [mapOverride]);
  const base = MAP[key] || {
    label: typ || FALLBACK_LABEL,
    cls: getFallbackCls()
  };
  const text = onlySuffix && suffix ? suffix : suffix ? `${base.label} – ${suffix}` : base.label;
  const tooltip = titleProp || text;
  const badgeSize = size === "status" ? "status" : small ? "xs" : "sm";
  return <PillBadge label={text} ariaLabel={`Typ: ${text}`} title={tooltip} toneClassName={base.cls} size={badgeSize} pulse={pulse} icon={icon} className={className} />;
}